import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setSubscriptionTier } from '../../store/slices/userSlice';
import { CText, SEO } from '../../components/common';
import {
  FiCheck,
  FiX,
  FiArrowLeft,
  FiZap,
  FiMessageSquare,
  FiGrid,
  FiUsers,
  FiSearch,
  FiBell,
  FiHeadphones,
} from 'react-icons/fi';
import routes from '../../utils/routes';
import { getAuthToken } from '../../utils/localStorage';
import {
  fetchSubscriptionPricing,
  type SubscriptionTenure,
  type TenurePricingInfo,
} from '../../api/expert';
import CheckoutModal from '../../components/subscription/CheckoutModal';

type PlanType = 'free' | 'pro' | 'premium';

interface PlanFeature {
  name: string;
  free: string | boolean;
  pro: string | boolean;
  premium: string | boolean;
  icon: any;
}

const TENURE_OPTIONS: { key: SubscriptionTenure; label: string; badge?: string }[] = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'half_yearly', label: 'Half-Yearly', badge: 'Save 10%' },
  { key: 'yearly', label: 'Yearly', badge: 'Save 20%' },
];

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const currentTier = useAppSelector((state) => state.user.subscription.tier);

  const [selectedPlan, setSelectedPlan] = useState<PlanType>('pro');
  const [selectedTenure, setSelectedTenure] = useState<SubscriptionTenure>('monthly');
  const [showCheckout, setShowCheckout] = useState(false);
  const [pricingData, setPricingData] = useState<Record<string, TenurePricingInfo[]>>({});

  useEffect(() => {
    fetchSubscriptionPricing()
      .then((res) => setPricingData(res.pricing))
      .catch(() => {});
  }, []);

  const getPlanPrice = (planId: PlanType): { monthly: number; total: number; days: number } => {
    if (planId === 'free') return { monthly: 0, total: 0, days: 0 };
    const tenureData = pricingData[planId]?.find((t) => t.tenure === selectedTenure);
    if (tenureData) {
      return {
        monthly: tenureData.monthly_equivalent,
        total: tenureData.total_price,
        days: tenureData.days,
      };
    }
    // Fallback
    const fallback = planId === 'pro' ? 499 : 999;
    return { monthly: fallback, total: fallback, days: 30 };
  };

  const plans = [
    {
      id: 'free' as PlanType,
      name: 'Free',
      description: 'Get started with basic features',
      highlight: false,
    },
    {
      id: 'pro' as PlanType,
      name: 'Pro',
      description: 'Unlock the full power of Yantrai',
      highlight: true,
      badge: 'Most Popular',
    },
    {
      id: 'premium' as PlanType,
      name: 'Premium',
      description: 'For serious investors',
      highlight: false,
    },
  ];

  const features: PlanFeature[] = [
    {
      name: 'AI Copilot Queries',
      icon: FiMessageSquare,
      free: '5/week',
      pro: 'Unlimited',
      premium: 'Unlimited',
    },
    {
      name: 'Deep Search (Full PDFs)',
      icon: FiSearch,
      free: false,
      pro: true,
      premium: true,
    },
    {
      name: 'Investment Buckets',
      icon: FiGrid,
      free: '2 buckets',
      pro: 'All 15+ buckets',
      premium: 'All + alerts',
    },
    {
      name: 'Bucket Price Alerts',
      icon: FiBell,
      free: false,
      pro: false,
      premium: true,
    },
    {
      name: 'Expert Advisor Discount',
      icon: FiUsers,
      free: false,
      pro: '10% off',
      premium: '25% off + 1 free',
    },
    {
      name: 'Priority Support',
      icon: FiHeadphones,
      free: false,
      pro: false,
      premium: true,
    },
  ];

  const handleSubscribe = () => {
    if (selectedPlan === currentTier) return;

    if (selectedPlan === 'free') {
      dispatch(setSubscriptionTier(selectedPlan));
      navigate(routes.home);
      return;
    }

    if (!getAuthToken()) {
      navigate(routes.login, { state: { from: routes.pricing } });
      return;
    }

    setShowCheckout(true);
  };

  const handleCheckoutSuccess = (data: {
    subscription_tier: string;
    subscription_tenure: string;
    subscription_expires: string;
    wallet_balance: string;
  }) => {
    dispatch(setSubscriptionTier(data.subscription_tier as PlanType));
    setShowCheckout(false);
    navigate(routes.home);
  };

  const renderFeatureValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center mx-auto"
          style={{ backgroundColor: colors.primary + '20' }}
        >
          {React.createElement(FiCheck as any, { size: 14, color: colors.primary })}
        </div>
      ) : (
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center mx-auto"
          style={{ backgroundColor: colors.grayScale400 + '30' }}
        >
          {React.createElement(FiX as any, { size: 14, color: colors.grayScale400 })}
        </div>
      );
    }
    return (
      <CText type="r12" color={colors.textColor} style={{ fontWeight: 500 }}>
        {value}
      </CText>
    );
  };

  return (
    <div
      className="min-h-screen pb-8"
      style={{ backgroundColor: colors.bgColor || colors.backgroundColor }}
    >
      <SEO
        title="Pricing Plans"
        description="Choose the right Yantrai plan for your investment needs. Free, Pro (Rs.499/mo), or Premium (Rs.999/mo) with AI research, expert advice, and more."
        canonical="/pricing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Yantrai Investment Platform',
          description: 'AI-powered investment platform with stock research, expert advice, and portfolio tracking.',
          offers: [
            { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'INR' },
            { '@type': 'Offer', name: 'Pro', price: '499', priceCurrency: 'INR' },
            { '@type': 'Offer', name: 'Premium', price: '999', priceCurrency: 'INR' },
          ],
        }}
      />
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-4 py-4 border-b"
        style={{
          backgroundColor: colors.glassBackground || colors.backgroundColor,
          borderColor: colors.borderColor,
          backdropFilter: 'blur(12px)',
        }}
      >
        <div className="lg:max-w-4xl lg:mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl transition-all duration-200 hover:opacity-80"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(FiArrowLeft as any, { size: 20, color: colors.textColor })}
          </button>
          <div>
            <CText type="B18" color={colors.textColor}>
              Choose Your Plan
            </CText>
            <CText type="r12" color={colors.grayScale500}>
              Unlock premium features
            </CText>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-8 lg:max-w-4xl lg:mx-auto">
        {/* Hero */}
        <div className="text-center mb-6">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              boxShadow: colors.shadowGlow,
            }}
          >
            {React.createElement(FiZap as any, { size: 32, color: '#fff' })}
          </div>
          <h1 className="text-2xl font-bold gradient-text mb-2">Upgrade to Pro</h1>
          <CText type="r14" color={colors.grayScale500}>
            Get unlimited AI queries, all buckets, and exclusive expert discounts
          </CText>
        </div>

        {/* Tenure Toggle */}
        <div
          className="flex items-center justify-center gap-1 p-1 rounded-xl mb-8 mx-auto"
          style={{
            backgroundColor: colors.inputBackground,
            border: `1px solid ${colors.borderColor}`,
            maxWidth: 420,
          }}
        >
          {TENURE_OPTIONS.map((option) => {
            const isActive = selectedTenure === option.key;
            return (
              <button
                key={option.key}
                onClick={() => setSelectedTenure(option.key)}
                className="flex-1 py-2.5 px-3 rounded-lg text-center transition-all duration-200 relative"
                style={{
                  backgroundColor: isActive ? colors.primary : 'transparent',
                  color: isActive ? '#fff' : colors.textColor,
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 13,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {option.label}
                {option.badge && (
                  <span
                    className="absolute -top-2.5 right-0 px-1.5 py-0.5 rounded text-[9px] font-bold"
                    style={{
                      backgroundColor: '#10B981',
                      color: '#fff',
                      lineHeight: '1',
                    }}
                  >
                    {option.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const isCurrent = currentTier === plan.id;
            const price = getPlanPrice(plan.id);

            return (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-5 rounded-2xl text-left transition-all duration-200 ${
                  isSelected ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                }`}
                style={{
                  backgroundColor: colors.cardBackground || colors.inputBackground,
                  border: isSelected
                    ? `2px solid ${colors.primary}`
                    : `1px solid ${colors.borderColor}`,
                  boxShadow: isSelected ? colors.shadowLarge : colors.shadowSoft,
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
                      color: '#fff',
                    }}
                  >
                    {plan.badge}
                  </div>
                )}

                {/* Current Badge */}
                {isCurrent && (
                  <div
                    className="absolute top-3 right-3 px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: colors.primary + '20',
                      color: colors.primary,
                    }}
                  >
                    Current
                  </div>
                )}

                <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
                  {plan.name}
                </CText>

                <div className="flex items-baseline gap-1 mt-2 mb-1">
                  <CText type="B24" color={colors.textColor} style={{ fontWeight: 700 }}>
                    {price.monthly === 0 ? 'Free' : `\u20B9${price.monthly}`}
                  </CText>
                  {price.monthly > 0 && (
                    <CText type="r12" color={colors.grayScale500}>
                      /mo
                    </CText>
                  )}
                </div>

                {price.total > 0 && selectedTenure !== 'monthly' && (
                  <CText type="r12" color={colors.grayScale500} style={{ marginBottom: 4 }}>
                    {`\u20B9${price.total.toLocaleString('en-IN')} total for ${price.days} days`}
                  </CText>
                )}

                <CText type="r12" color={colors.grayScale500}>
                  {plan.description}
                </CText>

                {/* Selection indicator */}
                <div
                  className="mt-4 py-2 rounded-lg text-center text-sm font-semibold transition-all duration-200"
                  style={{
                    backgroundColor: isSelected ? colors.primary : colors.primary + '15',
                    color: isSelected ? '#fff' : colors.primary,
                  }}
                >
                  {isSelected ? 'Selected' : 'Select'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Features Comparison */}
        <div
          className="rounded-2xl overflow-hidden card-shadow"
          style={{
            backgroundColor: colors.cardBackground || colors.inputBackground,
            border: `1px solid ${colors.borderColor}`,
          }}
        >
          <div
            className="p-4 border-b"
            style={{ borderColor: colors.borderColor }}
          >
            <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
              Compare Features
            </CText>
          </div>

          {/* Feature Rows */}
          {features.map((feature, idx) => (
            <div
              key={feature.name}
              className="grid grid-cols-4 gap-2 p-4 items-center"
              style={{
                backgroundColor: idx % 2 === 0 ? colors.backgroundColor : 'transparent',
                borderBottom: idx < features.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
              }}
            >
              <div className="flex items-center gap-2 col-span-1">
                {React.createElement(feature.icon as any, {
                  size: 16,
                  color: colors.grayScale500,
                })}
                <CText type="r12" color={colors.textColor}>
                  {feature.name}
                </CText>
              </div>
              <div className="text-center">{renderFeatureValue(feature.free)}</div>
              <div className="text-center">{renderFeatureValue(feature.pro)}</div>
              <div className="text-center">{renderFeatureValue(feature.premium)}</div>
            </div>
          ))}

          {/* Column Headers */}
          <div
            className="grid grid-cols-4 gap-2 p-4 border-t"
            style={{
              backgroundColor: colors.primary + '10',
              borderColor: colors.borderColor,
            }}
          >
            <div />
            <div className="text-center">
              <CText type="M12" color={colors.grayScale500}>
                Free
              </CText>
            </div>
            <div className="text-center">
              <CText type="M12" color={colors.primary} style={{ fontWeight: 600 }}>
                Pro
              </CText>
            </div>
            <div className="text-center">
              <CText type="M12" color={colors.grayScale500}>
                Premium
              </CText>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-8">
          <button
            onClick={handleSubscribe}
            disabled={selectedPlan === currentTier}
            className="w-full py-4 rounded-2xl font-semibold text-lg transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-50"
            style={{
              background:
                selectedPlan === currentTier
                  ? colors.grayScale400
                  : `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              border: 'none',
              cursor: selectedPlan === currentTier ? 'not-allowed' : 'pointer',
              boxShadow: selectedPlan === currentTier ? 'none' : colors.shadowLarge,
            }}
          >
            {selectedPlan === currentTier
              ? 'Current Plan'
              : selectedPlan === 'free'
              ? 'Continue with Free'
              : `Subscribe - \u20B9${getPlanPrice(selectedPlan).monthly}/mo`}
          </button>

          {selectedPlan !== 'free' && selectedPlan !== currentTier && (
            <CText
              type="r12"
              color={colors.grayScale500}
              style={{ textAlign: 'center', marginTop: 12, display: 'block' }}
            >
              Pay from wallet or Razorpay. Cancel anytime.
            </CText>
          )}
        </div>

        {/* Trust Section */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-6">
            <CText type="r10" color={colors.grayScale500}>
              Secure Payment
            </CText>
            <CText type="r10" color={colors.grayScale500}>
              Instant Activation
            </CText>
            <CText type="r10" color={colors.grayScale500}>
              Cancel Anytime
            </CText>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      {selectedPlan !== 'free' && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          plan={selectedPlan as 'pro' | 'premium'}
          tenure={selectedTenure}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </div>
  );
};

export default Pricing;
